"use client";

import { useCallback, useEffect, useRef } from "react";
import { type ParsedAddress } from "./AddressAutocomplete";
import type { ZipLocation } from "@/hooks/useZipGeocoding";
import { AddressInputFullForm } from "./address-input-full-form";
import type {
  AddressValue,
  AddressFieldErrors,
  AddressFieldKey,
} from "./address-input-types";
import { getRequestDeliveryAddressErrors } from "@shared/validation/address";
import { ALL_REGIONS } from "@shared/formats/state";

export type {
  AddressValue,
  AddressFieldErrors,
  AddressFieldKey,
} from "./address-input-types";

export interface AddressInputProps {
  value: AddressValue;
  onChange: (address: AddressValue) => void;
  onGeocode?: (result: ZipLocation) => void;
  variant?: "full" | "compact" | "inline";
  layout?: "paired";
  hiddenFields?: ReadonlyArray<AddressFieldKey>;
  showLine2?: boolean;
  showLabels?: boolean;
  disabled?: boolean;
  errors?: AddressFieldErrors;
  required?: boolean;
  optionalFields?: ReadonlyArray<AddressFieldKey>;
  className?: string;
}

export function AddressInput(props: AddressInputProps) {
  const variant = props.variant ?? "full";
  const callbacks = useAddressInputCallbacks(props);
  const errors = {
    ...props.errors,
    ...getRequestDeliveryAddressErrors(props.value),
  };
  return (
    <AddressInputFullForm
      {...props}
      {...callbacks}
      streetDisplay={props.value.line1}
      isCompact={variant !== "full"}
      isInline={variant === "inline"}
      showLine2={(props.showLine2 ?? true) && variant !== "inline"}
      showFieldLabels={props.showLabels ?? variant !== "compact"}
      disabled={props.disabled ?? false}
      errors={errors}
      required={props.required ?? false}
    />
  );
}

function useAddressInputCallbacks({
  value,
  onChange,
  onGeocode,
}: AddressInputProps) {
  const onZipGeocode = useGuardedZipGeocode(value, onChange, onGeocode);
  const onStreetAutocomplete = (
    displayValue: string,
    parsed: ParsedAddress | null,
  ) => {
    onChange(
      parsed
        ? {
            line1: parsed.line1,
            line2: parsed.line2 ?? "",
            city: parsed.city,
            state: parsed.state,
            postalCode: parsed.zip,
            country: parsed.country || value.country,
          }
        : { ...value, line1: displayValue },
    );
  };
  const onStateChange = (state: string) => {
    const region = ALL_REGIONS.find((candidate) => candidate.code === state);
    const country = region?.country ?? value.country;
    onChange({
      ...value,
      state,
      country,
      postalCode:
        country === value.country && state === value.state
          ? value.postalCode
          : "",
    });
  };
  return {
    onStreetAutocomplete,
    onZipGeocode,
    onCityChange: (city: string) => onChange({ ...value, city }),
    onStateChange,
  };
}

function useGuardedZipGeocode(
  value: AddressValue,
  onChange: AddressInputProps["onChange"],
  onGeocode: AddressInputProps["onGeocode"],
) {
  const latestAddress = useRef(value);
  useEffect(() => {
    latestAddress.current = value;
  }, [value]);
  return useCallback(
    (result: ZipLocation | null) => {
      const current = latestAddress.current;
      if (
        !result ||
        current.postalCode !== value.postalCode ||
        current.country !== value.country ||
        current.city !== value.city ||
        current.state !== value.state
      )
        return;
      onChange({ ...current, city: result.city, state: result.state });
      onGeocode?.(result);
    },
    [onChange, value, onGeocode],
  );
}
